import React, { useState, useEffect } from 'react';
import { Popconfirm, Tag, Table, Form, Input, Select, Button, Space, Card, Col, message } from 'antd';
import FilterPanel from '../../components/common/FilterPanel';
import { useTranslation } from 'react-i18next';
import { warehouseService } from '../../services/warehouseService';

const Warehouse = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`
    });
    const { t } = useTranslation();


    const columns = [
        {
            title: t('warehouse.code'),
            dataIndex: 'code',
            key: 'code',
            width: 120,
        },
        {
            title: t('warehouse.name'),
            dataIndex: 'name',
            key: 'name',
            width: 150,
        },
        {
            title: t('warehouse.englishName'),
            dataIndex: 'englishName',
            key: 'englishName',
            width: 180,
        },
        {
            title: t('warehouse.contactName'),
            dataIndex: 'contactName',
            key: 'contactName',
            width: 100,
        },
        {
            title: t('warehouse.contactPhone'),
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            width: 130,
        },
        {
            title: t('warehouse.location'),
            key: 'location',
            width: 200,
            render: (_, record) => {
                const location = [record.state, record.city, record.subdistrict].filter(Boolean).join(' ');
                return location || '-';
            },
        },
        {
            title: t('warehouse.detailAddress'),
            dataIndex: 'detailAddress',
            key: 'detailAddress',
            width: 200,
            ellipsis: true,
        },
        {
            title: t('warehouse.area'),
            dataIndex: 'area',
            key: 'area',
            width: 100,
        },
        {
            title: t('warehouse.format'),
            dataIndex: 'format',
            key: 'format',
            width: 120,
            render: (format) => (
                <Tag>
                    {format}
                </Tag>
            ),
        },
        {
            title: t('warehouse.clusterSection'),
            dataIndex: 'clusterSection',
            key: 'clusterSection',
            width: 120,
            render: (clusterSection) => (
                <Tag>
                    {clusterSection}
                </Tag>
            ),
        },
        {
            title: t('warehouse.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => {
                const colorMap = {
                    'ACTIVE': 'green',
                    'INACTIVE': 'orange',
                    'DELETED': 'red'
                };
                const textMap = {
                    'ACTIVE': t('warehouse.active'),
                    'INACTIVE': t('warehouse.inactive'),
                    'DELETED': t('warehouse.deleted')
                };
                return (
                    <Tag color={colorMap[status]}>
                        {textMap[status] || status}
                    </Tag>
                );
            },
        }
    ];

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            const searchParams = {
                ...form.getFieldsValue(),
                currentPage: pagination.current,
                pageSize: pagination.pageSize,
                ...params
            };

            const response = await warehouseService.getWarehouses(searchParams);

            if (response.success) {
                setDataSource(response.data.tableData);
                setPagination(prev => ({
                    ...prev,
                    current: response.data.paging?.currentPage || 1,
                    total: response.data.paging?.totalCount || 0
                }));
            } else {
                message.error(response.message || t('warehouse.queryFailed'));
            }
        } catch (error) {
            console.error('查询失败:', error);
            message.error(t('warehouse.queryFailedRetry'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = (values) => {
        form.setFieldsValue(values);
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData({ currentPage: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData({ currentPage: 1 });
    };

    const handleTableChange = (paginationConfig) => {
        const newPagination = {
            ...pagination,
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        };
        setPagination(newPagination);
        fetchData({
            currentPage: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        });
    };

    return (
        <div className="warehouse-container">
            <FilterPanel
                form={form}
                onFilter={handleSearch}
                onReset={handleReset}
                initialValues={{}}
                title={t('menu.warehouse')}
                collapsible={true}
                defaultCollapsed={false}
                loading={loading}
            >
                <Col span={6}>
                    <Form.Item name="code" label={t('warehouse.code')}>
                        <Input placeholder={t('warehouse.codePlaceholder')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="name" label={t('warehouse.name')}>
                        <Input placeholder={t('warehouse.namePlaceholder')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="format" label={t('warehouse.format')}>
                        {/* <Select
                            placeholder={t('warehouse.formatPlaceholder')}
                            allowClear
                            options={formatOptions}
                        /> */}
                        <Input placeholder={t('warehouse.formatPlaceholder')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="clusterSection" label={t('warehouse.clusterSection')}>
                        <Input placeholder={t('warehouse.clusterSectionPlaceholder')} />
                    </Form.Item>
                </Col>
            </FilterPanel>

            <Card className="warehouse-table">
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    rowKey="id"
                    scroll={{ x: 1200, y: 500 }}
                />
            </Card>
        </div>
    );
};

export default Warehouse;